/**
 * One shared animation loop per avatar, so its animation systems get the same
 * deltaTime and batch their updates instead of each running its own RAF loop.
 */

import { useCallback, useEffect, useRef } from 'react';
import { useAnyDialogOpen } from '@/components/ui/dialogPresenceStore';

/**
 * Animation callback function type
 * @param deltaTime - Time elapsed since last frame in milliseconds
 */
type AnimationCallback = (deltaTime: number) => void;

/**
 * Return type for useAvatarRAF hook
 */
interface UseAvatarRAFReturn {
  /**
   * Subscribe to the animation loop
   * @param callback - Function to call on each animation frame
   * @returns Cleanup function to unsubscribe
   */
  subscribe: (callback: AnimationCallback) => () => void;
}

/**
 * Central RAF scheduler for avatar animations
 *
 * Provides a shared requestAnimationFrame loop that multiple animation systems
 * can subscribe to, improving performance and ensuring consistent timing.
 *
 * @returns Object with subscribe function for registering animation callbacks
 */
export function useAvatarRAF(): UseAvatarRAFReturn {
  const isPaused = useAnyDialogOpen();
  // Set of all active animation callbacks
  const callbacksRef = useRef<Set<AnimationCallback>>(new Set());

  // Current animation frame ID for cleanup
  const frameIdRef = useRef<number | null>(null);

  // Last frame timestamp for deltaTime calculation
  const lastTimeRef = useRef<number>(0);

  /**
   * Main animation loop
   * Calls all subscribed callbacks with consistent deltaTime
   */
  const animate = useCallback(
    (currentTime: number) => {
      // Calculate time elapsed since last frame
      const deltaTime =
        lastTimeRef.current === 0
          ? 16.67 // Initial frame assumes 60fps (16.67ms)
          : currentTime - lastTimeRef.current;

      lastTimeRef.current = currentTime;

      if (!isPaused) {
        // Call all subscribed animation callbacks
        callbacksRef.current.forEach((callback) => {
          try {
            callback(deltaTime);
          } catch (error) {
            console.error('Error in avatar animation callback:', error);
          }
        });
      }

      // Continue animation loop if we have active callbacks
      if (callbacksRef.current.size > 0) {
        frameIdRef.current = requestAnimationFrame(animate);
      } else {
        frameIdRef.current = null;
        lastTimeRef.current = 0;
      }
    },
    [isPaused]
  );

  /**
   * Subscribe a callback to the animation loop
   * Automatically starts the loop when the first callback is added
   */
  const subscribe = useCallback(
    (callback: AnimationCallback) => {
      const wasEmpty = callbacksRef.current.size === 0;
      callbacksRef.current.add(callback);

      // Start animation loop if this is the first callback
      if (wasEmpty && frameIdRef.current === null) {
        frameIdRef.current = requestAnimationFrame(animate);
      }

      // Return cleanup function
      return () => {
        callbacksRef.current.delete(callback);

        // Stop animation loop if no callbacks remain
        if (callbacksRef.current.size === 0 && frameIdRef.current !== null) {
          cancelAnimationFrame(frameIdRef.current);
          frameIdRef.current = null;
          lastTimeRef.current = 0;
        }
      };
    },
    [animate]
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (frameIdRef.current !== null) {
        cancelAnimationFrame(frameIdRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!isPaused) {
      // Avoid a large delta spike when resuming after pause.
      lastTimeRef.current = 0;
    }
  }, [isPaused]);

  return { subscribe };
}
